import type { Task } from '../meta/tasks';
import { WEARABLE_ORDER, type WearableId, type WearableSlot } from '../economy/wardrobe';
import { buildWearableCards, SLOT_LABEL, SLOTS } from './wardrobe';

/** Wave I (I-3): "[THIS RUN] coalesced session pickup log" — coins/scrap/part/huntsCompleted are deltas off a snapshot taken at boot/resume (main.ts), so they need no manual increment at every wallet call site; `cosmetics` is the one field that genuinely needs a push (main.ts's `grantWearables`, the single choke point for every cosmetic grant, both collection- and direct-grant-triggered). */
export interface SessionLog {
  coins: number;
  scrap: number;
  part: number;
  hearts: number;
  huntsCompleted: number;
  cosmetics: string[];
}

/** Review fix (HIGH, spec-fidelity): I-3's own spec line names `Progress.stats` lifetime counters as a required deliverable, distinct from the ephemeral `SessionLog` above — mirrors `meta/save.ts`'s `Progress['stats']` shape exactly (duplicated here rather than imported, matching every other callback in this file's own "pre-formatted/pre-shaped data in, no save-schema coupling" convention). */
export interface LifetimeStats {
  coins: number;
  scrap: number;
  parts: number;
  hearts: number;
  drops: number;
  killsByKind: Partial<Record<string, number>>;
}

export interface StashCallbacks {
  getWallet: () => { coins: number; scrap: number; part: number };
  /** Pre-formatted hint text per material — main.ts already owns every craft-tier constant (economy/craft.ts + its own ARMOR_* trio), so it composes "what does this feed next" itself rather than this screen re-deriving recipe internals. */
  getRecipeHints: () => { scrapHint: string; partHint: string };
  getTasks: () => Task[];
  getSessionLog: () => SessionLog;
  getLifetimeStats: () => LifetimeStats;
  isOwned: (id: WearableId) => boolean;
  getEquipped: (slot: WearableSlot) => WearableId | null;
  onEquip: (slot: WearableSlot, id: WearableId | null) => void;
  onClose: () => void;
}

/**
 * Wave I (PLAN-CITY3 §5, I-3: "THE STASH screen + the ledger"). Same
 * full-screen-overlay idiom as `ui/huntboard.ts`/`ui/wardrobe.ts` (reuses
 * `ui/pause.ts`'s own `.br3d-pausescreen-title`/`.br3d-menu-btn` classes
 * directly) — one scrolling column of four labeled sections rather than a
 * tab switcher, matching every other overlay in this file's existing
 * "stack it, let `.br3d-pausescreen`'s own `overflow-y:auto` handle a short
 * viewport" convention (Classic gotcha #6's lesson, still enforced here).
 * Driven by main.ts's single `uiState` (owns the `'stash'` value), reachable
 * from a third pause-menu button (field-accessible anywhere) AND the bodega
 * counter interactable (`'stash'` action, previously just a toast). Rebuilds
 * from CURRENT state every time it's shown, same "never cache a stale
 * snapshot" discipline every sibling screen already established.
 */
export class StashScreen {
  private readonly root: HTMLDivElement;
  private readonly body: HTMLDivElement;

  constructor(private readonly callbacks: StashCallbacks) {
    this.root = document.createElement('div');
    this.root.className = 'br3d-pausescreen br3d-stash';
    this.root.style.display = 'none';

    const title = document.createElement('div');
    title.className = 'br3d-pausescreen-title';
    title.textContent = 'THE STASH';

    this.body = document.createElement('div');
    this.body.className = 'br3d-stash-body';

    this.root.append(title, this.body, this.menuButton('BACK', () => this.callbacks.onClose()));
    document.body.append(this.root);
  }

  private menuButton(label: string, onPress: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.className = 'br3d-menu-btn';
    btn.textContent = label;
    btn.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      e.preventDefault();
      onPress();
    });
    return btn;
  }

  private sectionHeader(text: string): HTMLDivElement {
    const h = document.createElement('div');
    h.className = 'br3d-stash-section-header';
    h.textContent = text;
    return h;
  }

  private buildWalletSection(): HTMLDivElement {
    const section = document.createElement('div');
    section.className = 'br3d-stash-section';
    section.append(this.sectionHeader('WALLET'));
    const wallet = this.callbacks.getWallet();
    const hints = this.callbacks.getRecipeHints();
    const row = document.createElement('div');
    row.className = 'br3d-stash-wallet-row';
    const line = (icon: string, value: number, hint: string): HTMLDivElement => {
      const el = document.createElement('div');
      el.className = 'br3d-stash-wallet-line';
      const amount = document.createElement('div');
      amount.className = 'br3d-stash-wallet-amount';
      amount.textContent = `${icon} ${value}`;
      const feed = document.createElement('div');
      feed.className = 'br3d-stash-wallet-hint';
      feed.textContent = hint;
      el.append(amount, feed);
      return el;
    };
    row.append(line('●', wallet.coins, 'Coins — spent on every recipe tier'), line('⬡', wallet.scrap, hints.scrapHint), line('⚙', wallet.part, hints.partHint));
    section.append(row);
    return section;
  }

  private buildFitsSection(): HTMLDivElement {
    const section = document.createElement('div');
    section.className = 'br3d-stash-section';
    section.append(this.sectionHeader('STREET FITS'));
    const count = document.createElement('div');
    count.className = 'br3d-stash-fits-count';
    count.textContent = `${WEARABLE_ORDER.filter((id) => this.callbacks.isOwned(id)).length}/${WEARABLE_ORDER.length} owned`;
    section.append(count);
    for (const slot of SLOTS) {
      const row = document.createElement('div');
      row.className = 'br3d-wardrobe-row';
      const label = document.createElement('div');
      label.className = 'br3d-wardrobe-slot-label';
      label.textContent = SLOT_LABEL[slot];
      row.append(label);
      const equipped = this.callbacks.getEquipped(slot);
      row.append(buildWearableCards(slot, this.callbacks.isOwned, equipped, (id) => this.callbacks.onEquip(slot, id)));
      section.append(row);
    }
    return section;
  }

  private buildObjectivesSection(): HTMLDivElement {
    const section = document.createElement('div');
    section.className = 'br3d-stash-section';
    section.append(this.sectionHeader('OBJECTIVES'));
    const list = document.createElement('div');
    list.className = 'br3d-stash-tasklist';
    for (const task of this.callbacks.getTasks()) {
      const row = document.createElement('div');
      row.className = 'br3d-stash-task';
      row.classList.toggle('br3d-stash-task-done', task.done);
      const mark = document.createElement('span');
      mark.className = 'br3d-stash-task-mark';
      mark.textContent = task.done ? '✓' : '○';
      const label = document.createElement('span');
      label.textContent = task.label;
      row.append(mark, label);
      list.append(row);
    }
    section.append(list);
    return section;
  }

  private buildThisRunSection(): HTMLDivElement {
    const section = document.createElement('div');
    section.className = 'br3d-stash-section';
    section.append(this.sectionHeader('THIS RUN'));
    const log = this.callbacks.getSessionLog();
    const list = document.createElement('div');
    list.className = 'br3d-stash-runlog';
    const entries: string[] = [];
    if (log.coins > 0) entries.push(`● +${log.coins} coins`);
    if (log.scrap > 0) entries.push(`⬡ +${log.scrap} scrap`);
    if (log.part > 0) entries.push(`⚙ +${log.part} parts`);
    if (log.hearts > 0) entries.push(`❤️ +${log.hearts} hearts collected`);
    if (log.huntsCompleted > 0) entries.push(`🎯 ${log.huntsCompleted} hunt${log.huntsCompleted === 1 ? '' : 's'} completed`);
    for (const name of log.cosmetics) entries.push(`✨ ${name}`);
    if (entries.length === 0) entries.push('Nothing picked up yet this session.');
    for (const text of entries) {
      const row = document.createElement('div');
      row.className = 'br3d-stash-runlog-line';
      row.textContent = text;
      list.append(row);
    }
    section.append(list);

    // Review fix (HIGH, spec-fidelity): the durable half of I-3's own
    // "Progress.stats lifetime counters" line — [THIS RUN] above is a
    // per-session delta that resets on reload, so the lifetime total needed
    // its own visible home too, not just the save field itself existing.
    const lifetime = this.callbacks.getLifetimeStats();
    const lifetimeHeader = document.createElement('div');
    lifetimeHeader.className = 'br3d-stash-lifetime-header';
    lifetimeHeader.textContent = 'LIFETIME';
    let totalKills = 0;
    for (const count of Object.values(lifetime.killsByKind)) totalKills += count ?? 0;
    const lifetimeLine = document.createElement('div');
    lifetimeLine.className = 'br3d-stash-runlog-line';
    lifetimeLine.textContent = `● ${lifetime.coins} · ⬡ ${lifetime.scrap} · ⚙ ${lifetime.parts} · ❤️ ${lifetime.hearts} · 📦 ${lifetime.drops} drops · 💀 ${totalKills} kills`;
    section.append(lifetimeHeader, lifetimeLine);
    return section;
  }

  private refresh(): void {
    this.body.replaceChildren(this.buildWalletSection(), this.buildFitsSection(), this.buildObjectivesSection(), this.buildThisRunSection());
  }

  /** Called by main.ts's single `setUiState()`, alongside `pauseMenu.setVisible()`/`huntBoard.setVisible()`/`wardrobeScreen.setVisible()` — see those functions' own comments. */
  setVisible(visible: boolean): void {
    if (visible) this.refresh();
    this.root.style.display = visible ? 'flex' : 'none';
  }
}
